import React, { useState, useRef, useEffect, useMemo } from "react";
import "../stylez/AddClassPage.css";
import { ButtonGroup } from "react-bootstrap";
import useScheduleMaker from "../react-query/useScheduleMaker";
import EventList from "../components/ScheduleComponents/EventList";
import { useEvents } from "../react-query/useEvents";
import { useOverlapSettings } from "../react-query/useOverlapSettings";
import { useAuth } from "../react-query/useAuth";
import OverlapConfirmationModal from "../components/ScheduleComponents/OverlapConfirmationModal";
import CoachCountList from "../components/ScheduleMakerComponents/CoachCountList";
import { deleteEvent as deleteEventRequest } from "../services/api_data";
import { toast } from "react-toastify";
import {
  FaCalendarDay,
  FaCalendarWeek,
  FaTrash,
  FaPlus,
  FaCalendarAlt,
  FaClock,
  FaExclamationTriangle,
  FaUserFriends,
} from "react-icons/fa";
import { FiChevronDown } from "react-icons/fi";
import { ACTIVE_CLASS_TYPES as classTypes } from "../constants/classTypes";
import { motion, AnimatePresence } from "framer-motion";
import { format, addMinutes, parse } from "date-fns";
import {
  checkOverlapWithDuration,
  formatOverlapWarning,
  formatRecurringOverlapWarning,
} from "../functions/overlapDetection";

// Duration presets in minutes
const DURATION_PRESETS = [30, 45, 60, 75, 90];
const DEFAULT_DURATION = 60;
const WEEKDAY_NAMES = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
];

const AddClassPage = () => {
  const [selectedDate, setSelectedDate] = useState(
    new Date().toISOString().split("T")[0]
  );
  const { handleCreateSchedule, handleDeleteEvent } = useScheduleMaker();
  const { events, isLoading, isError } = useEvents({
    startDate: selectedDate,
    fetchType: "singleDay",
  });

  const { settings: overlapSettings } = useOverlapSettings();
  const { userData } = useAuth();
  const [selectedButton, setSelectedButton] = useState(null);
  const [selectedDays, setSelectedDays] = useState(new Array(7).fill(false));
  // Of the selected days, which need two coaches (Head and Co-coach)
  const [twoCoachDays, setTwoCoachDays] = useState(new Array(7).fill(false));
  const [coachCountDate, setCoachCountDate] = useState(
    format(new Date(), "yyyy-MM-dd")
  );
  const inputContainerRef = useRef(null);
  const reoccurringInputContainerRef = useRef(null);

  // Modal state
  const [showOverlapModal, setShowOverlapModal] = useState(false);
  const [pendingSubmission, setPendingSubmission] = useState(null);

  const [singleClassDetails, setSingleClassDetails] = useState({
    classType: "",
    date: selectedDate,
    startTime: "",
    duration: DEFAULT_DURATION,
    useCustomDuration: false,
  });

  const [recurringClassDetails, setRecurringClassDetails] = useState({
    classType: "",
    startDate: selectedDate,
    startTime: "",
    duration: DEFAULT_DURATION,
    useCustomDuration: false,
  });

  // Helper to calculate end date for the week of a given start date
  const getWeekRange = (dateStr) => {
    if (!dateStr) return { start: null, end: null };
    try {
      // Create date at noon to avoid timezone issues
      const [y, m, d] = dateStr.split("-").map(Number);
      const date = new Date(y, m - 1, d, 12, 0, 0);

      const day = date.getDay(); // 0 is Sunday
      const diff = date.getDate() - day; // Adjust to Sunday

      const start = new Date(date);
      start.setDate(diff);

      const end = new Date(start);
      end.setDate(start.getDate() + 6); // Add 6 days to get Saturday

      // Format as YYYY-MM-DD
      const formatDate = (d) => {
        const year = d.getFullYear();
        const month = String(d.getMonth() + 1).padStart(2, "0");
        const day = String(d.getDate()).padStart(2, "0");
        return `${year}-${month}-${day}`;
      };

      return {
        start: formatDate(start),
        end: formatDate(end),
      };
    } catch (e) {
      console.error("Error calculating week range:", e);
      return { start: null, end: null };
    }
  };

  // Calculate week range for the recurring start date
  const recurringWeekRange = useMemo(() => {
    return getWeekRange(recurringClassDetails.startDate);
  }, [recurringClassDetails.startDate]);

  // Fetch events for the week of the recurring start date
  const { events: recurringWeekEvents } = useEvents({
    startDate: recurringWeekRange.start,
    endDate: recurringWeekRange.end,
    fetchType: "weekly",
  });

  // Compute end time from start + duration
  const computeEndTime = (startTime, durationMinutes) => {
    if (!startTime) return "";
    try {
      const baseDate = parse(startTime, "HH:mm", new Date());
      const endDate = addMinutes(baseDate, durationMinutes);
      return format(endDate, "HH:mm");
    } catch {
      return "";
    }
  };

  // Formatted end time preview for display
  const formatTimePreview = (timeStr) => {
    if (!timeStr) return "--:--";
    try {
      const date = parse(timeStr, "HH:mm", new Date());
      return format(date, "h:mm a");
    } catch {
      return timeStr;
    }
  };

  // Computed end times
  const singleEndTime = useMemo(
    () =>
      computeEndTime(singleClassDetails.startTime, singleClassDetails.duration),
    [singleClassDetails.startTime, singleClassDetails.duration]
  );

  const recurringEndTime = useMemo(
    () =>
      computeEndTime(
        recurringClassDetails.startTime,
        recurringClassDetails.duration
      ),
    [recurringClassDetails.startTime, recurringClassDetails.duration]
  );

  const todaysEvents = events?.events ?? [];

  // Merge current view events with recurring week events for overlap checking
  const allScheduleEvents = useMemo(() => {
    // Current day events (from main view)
    const currentViewEvents = [
      ...(events?.events ?? []),
      ...(events?.availableEvents ?? []),
    ];

    // Recurring week events (fetched in background)
    // Note: useEvents with 'weekly' might return a different structure depending on API
    // Based on useEvents.js, 'weekly' returns data processed by processEvents
    // If processEvents returns an array for weekly, we can spread it.
    // If it returns { events: [], availableEvents: [] }, we handle that.

    let weeklyEvents = [];
    if (recurringWeekEvents) {
      if (Array.isArray(recurringWeekEvents)) {
        weeklyEvents = recurringWeekEvents;
      } else if (recurringWeekEvents.events) {
        weeklyEvents = [
          ...(recurringWeekEvents.events || []),
          ...(recurringWeekEvents.availableEvents || []),
        ];
      }
    }

    // Combine and deduplicate
    const combined = [...currentViewEvents, ...weeklyEvents];

    // Create unique keys for deduplication
    // For events with _id, use that. For generated events without _id, use start+type
    const uniqueEvents = Array.from(
      new Map(
        combined.map((item) => {
          const key =
            item._id || `${item.start}-${item.type || item.classType || ""}`;
          return [key, item];
        })
      ).values()
    );

    return uniqueEvents;
  }, [events, recurringWeekEvents]);

  // DEBUG: Log the events data structure
  useEffect(() => {
    console.log("=== OVERLAP DEBUG ===");
    console.log("Current selectedDate (fetching for):", selectedDate);
    console.log("Recurring Start Date:", recurringClassDetails.startDate);
    console.log("Recurring Week Range:", recurringWeekRange);
    console.log("Recurring Week Events:", recurringWeekEvents);
    console.log("All Combined Events:", allScheduleEvents?.length);
  }, [
    events,
    recurringWeekEvents,
    allScheduleEvents,
    selectedDate,
    recurringClassDetails.startDate,
    recurringWeekRange,
  ]);

  // Overlap detection for single class
  const singleClassOverlap = useMemo(() => {
    if (!singleClassDetails.startTime || !singleClassDetails.date) {
      return { hasOverlap: false, overlappingEvents: [] };
    }
    const result = checkOverlapWithDuration(
      singleClassDetails.startTime,
      singleClassDetails.duration,
      singleClassDetails.date,
      allScheduleEvents
    );

    // DEBUG: Log overlap detection results
    console.log("=== OVERLAP RESULT ===");
    console.log("Input:", {
      startTime: singleClassDetails.startTime,
      duration: singleClassDetails.duration,
      date: singleClassDetails.date,
    });
    console.log("Result:", result);
    console.log(
      "Overlapping events details:",
      result.overlappingEvents.map((e) => ({
        title: e.title,
        type: e.type,
        classType: e.classType,
        start: e.start,
        end: e.end,
      }))
    );

    return result;
  }, [
    singleClassDetails.startTime,
    singleClassDetails.duration,
    singleClassDetails.date,
    allScheduleEvents,
  ]);

  // Overlap detection for recurring class (checks ALL selected days)
  const recurringClassOverlap = useMemo(() => {
    if (!recurringClassDetails.startTime || !recurringClassDetails.startDate) {
      return { hasOverlap: false, overlappingEvents: [] };
    }

    // Check if any days are selected
    const hasSelectedDays = selectedDays.some((day) => day);
    if (!hasSelectedDays) {
      // If no days selected, just check the start date
      return checkOverlapWithDuration(
        recurringClassDetails.startTime,
        recurringClassDetails.duration,
        recurringClassDetails.startDate,
        allScheduleEvents
      );
    }

    // Check overlaps for each selected day
    const allOverlappingEvents = [];
    const dayNames = [
      "Sunday",
      "Monday",
      "Tuesday",
      "Wednesday",
      "Thursday",
      "Friday",
      "Saturday",
    ];

    // Create a date object from the input string YYYY-MM-DD
    // Using simple split to avoid timezone issues when constructing Date
    const [year, month, day] = recurringClassDetails.startDate
      .split("-")
      .map(Number);
    // Create date at noon to avoid timezone shifts
    const startDate = new Date(year, month - 1, day, 12, 0, 0);
    const startDayOfWeek = startDate.getDay();

    // Check each selected day
    selectedDays.forEach((isSelected, dayIndex) => {
      if (!isSelected) return;

      // Calculate the date offset for this day
      let daysToAdd = dayIndex - startDayOfWeek;
      if (daysToAdd < 0) daysToAdd += 7; // Move to next week if day is earlier in week

      // Create date for this occurrence
      const occurrenceDate = new Date(startDate);
      occurrenceDate.setDate(startDate.getDate() + daysToAdd);

      // Format YYYY-MM-DD manually to ensure local date is used
      const yyyy = occurrenceDate.getFullYear();
      const mm = String(occurrenceDate.getMonth() + 1).padStart(2, "0");
      const dd = String(occurrenceDate.getDate()).padStart(2, "0");
      const dateStr = `${yyyy}-${mm}-${dd}`;

      // Check overlap for this specific day
      const result = checkOverlapWithDuration(
        recurringClassDetails.startTime,
        recurringClassDetails.duration,
        dateStr,
        allScheduleEvents
      );

      // Add day name to each overlapping event for better reporting
      if (result.hasOverlap) {
        result.overlappingEvents.forEach((event) => {
          allOverlappingEvents.push({
            ...event,
            dayOfWeek: dayNames[dayIndex],
          });
        });
      }
    });

    return {
      hasOverlap: allOverlappingEvents.length > 0,
      overlappingEvents: allOverlappingEvents,
    };
  }, [
    recurringClassDetails.startTime,
    recurringClassDetails.duration,
    recurringClassDetails.startDate,
    selectedDays,
    allScheduleEvents,
  ]);
  const available_events = events?.availableEvents ?? [];
  // A two-coach class can have one document per role (Head and Co-coach). Show one
  // card per class and keep the other documents' ids so deleting the class
  // removes all of them, not just the first.
  const combinedEvents = Array.from(
    [...todaysEvents, ...available_events]
      .reduce((byClass, ev) => {
        const key = `${ev.start}-${ev.type}`;
        const existing = byClass.get(key);
        if (!existing) {
          byClass.set(key, { ...ev, siblingIds: [] });
        } else if (ev._id) {
          if (existing._id) {
            existing.siblingIds.push(ev._id);
          } else {
            // Prefer a real document over a virtual one as the card
            byClass.set(key, { ...ev, siblingIds: existing.siblingIds });
          }
        }
        return byClass;
      }, new Map())
      .values()
  );

  // Handle date change
  const handleDateChange = async (e) => {
    const newDate = e.target.value;
    setSelectedDate(newDate);
  };

  // Handle input changes
  const handleSingleClassInputChange = (e) => {
    const { name, value } = e.target;
    setSingleClassDetails({
      ...singleClassDetails,
      [name]: value,
    });
    // When date changes, also update selectedDate to fetch events for that date
    if (name === "date") {
      setSelectedDate(value);
    }
  };

  const handleRecurringClassInputChange = (e) => {
    setRecurringClassDetails({
      ...recurringClassDetails,
      [e.target.name]: e.target.value,
    });
  };

  // Check if user can create overlaps
  const canCreateOverlap = () => {
    const policy = overlapSettings?.overlap_policy || "all";
    if (policy === "none") return false;
    if (policy === "admins_only") return userData?.is_admin || false;
    return true; // "all"
  };

  // Check if confirmation is required
  const needsConfirmation = () => {
    return overlapSettings?.require_confirmation !== false; // Default to true
  };

  // Submit functions
  const submitSingleClass = async () => {
    try {
      const { classType, date, startTime, duration } = singleClassDetails;
      // Compute end time from start + duration
      const endTime = singleEndTime;

      if (!classType || !startTime || !endTime) {
        console.error("Missing required fields");
        return;
      }

      // Check for overlaps and handle based on policy
      if (singleClassOverlap.hasOverlap) {
        if (!canCreateOverlap()) {
          console.error("Overlap detected but user cannot create overlaps");
          toast.error(
            "❌ Overlapping classes are not allowed based on current policy settings."
          );
          return;
        }

        // If confirmation is required, show modal
        if (needsConfirmation()) {
          setPendingSubmission({
            type: "single",
            data: { classType, date, startTime, endTime, duration },
          });
          setShowOverlapModal(true);
          return;
        }
        // Otherwise, proceed without confirmation
      }

      // Execute submission
      const nxt = 0; // Placeholder, adjust as necessary
      await handleCreateSchedule(
        classType,
        false,
        startTime,
        endTime,
        date,
        nxt,
        [],
        duration
      );

      // Reset form after successful submission
      setSingleClassDetails({
        ...singleClassDetails,
        classType: "",
        startTime: "",
        duration: DEFAULT_DURATION,
        useCustomDuration: false,
      });

      console.log("Single Class Submitted with duration:", duration, "minutes");
    } catch (error) {
      console.error("Error submitting single class:", error);
    }
  };

  const submitRecurringClass = async () => {
    try {
      const { classType, startDate, startTime, duration } =
        recurringClassDetails;
      // Compute end time from start + duration
      const endTime = recurringEndTime;

      if (!classType || !startTime || !endTime) {
        console.error("Missing required fields");
        return;
      }

      // Convert selectedDays to day names
      const selectedDayNames = selectedDays.reduce((acc, isSelected, index) => {
        if (isSelected)
          acc.push(
            [
              "Sunday",
              "Monday",
              "Tuesday",
              "Wednesday",
              "Thursday",
              "Friday",
              "Saturday",
            ][index]
          );
        return acc;
      }, []);
      const twoCoachDayNames = selectedDayNames.filter(
        (name) => twoCoachDays[WEEKDAY_NAMES.indexOf(name)]
      );

      // Check for overlaps and handle based on policy
      if (recurringClassOverlap.hasOverlap) {
        if (!canCreateOverlap()) {
          console.error("Overlap detected but user cannot create overlaps");
          toast.error(
            "❌ Overlapping classes are not allowed based on current policy settings."
          );
          return;
        }

        // If confirmation is required, show modal
        if (needsConfirmation()) {
          setPendingSubmission({
            type: "recurring",
            data: {
              classType,
              startDate,
              startTime,
              endTime,
              duration,
              selectedDayNames,
              twoCoachDayNames,
            },
          });
          setShowOverlapModal(true);
          return;
        }
        // Otherwise, proceed without confirmation
      }

      // Execute submission
      await handleCreateSchedule(
        classType,
        true,
        startTime,
        endTime,
        startDate,
        0,
        selectedDayNames,
        duration,
        twoCoachDayNames
      );

      // Reset form after submission
      setRecurringClassDetails({
        ...recurringClassDetails,
        classType: "",
        startTime: "",
        duration: DEFAULT_DURATION,
        useCustomDuration: false,
      });
      setSelectedDays(new Array(7).fill(false));
      setTwoCoachDays(new Array(7).fill(false));

      console.log(
        "Recurring Class Submitted with duration:",
        duration,
        "minutes"
      );
    } catch (error) {
      console.error("Error submitting recurring class:", error);
    }
  };

  // Handle confirmed overlap creation
  const handleConfirmOverlap = async () => {
    if (!pendingSubmission) return;

    try {
      if (pendingSubmission.type === "single") {
        const { classType, date, startTime, endTime, duration } =
          pendingSubmission.data;
        await handleCreateSchedule(
          classType,
          false,
          startTime,
          endTime,
          date,
          0,
          [],
          duration
        );
        // Reset form
        setSingleClassDetails({
          ...singleClassDetails,
          classType: "",
          startTime: "",
          duration: DEFAULT_DURATION,
          useCustomDuration: false,
        });
      } else if (pendingSubmission.type === "recurring") {
        const {
          classType,
          startDate,
          startTime,
          endTime,
          duration,
          selectedDayNames,
          twoCoachDayNames = [],
        } = pendingSubmission.data;
        await handleCreateSchedule(
          classType,
          true,
          startTime,
          endTime,
          startDate,
          0,
          selectedDayNames,
          duration,
          twoCoachDayNames
        );
        // Reset form
        setRecurringClassDetails({
          ...recurringClassDetails,
          classType: "",
          startTime: "",
          duration: DEFAULT_DURATION,
          useCustomDuration: false,
        });
        setSelectedDays(new Array(7).fill(false));
        setTwoCoachDays(new Array(7).fill(false));
      }

      // Close modal and clear pending submission
      setShowOverlapModal(false);
      setPendingSubmission(null);
    } catch (error) {
      console.error("Error creating class with overlap:", error);
      setShowOverlapModal(false);
      setPendingSubmission(null);
    }
  };

  // Delete an event
  const deleteEvent = async (event, all) => {
    try {
      await handleDeleteEvent(event, all);
      // Deleting all future classes already covers every role's document
      if (!all && event.siblingIds?.length) {
        for (const siblingId of event.siblingIds) {
          await deleteEventRequest({ eventObj: { ...event, _id: siblingId }, all: false });
        }
      }
      console.log("Event deleted:", event);
    } catch (error) {
      console.error("Error deleting event:", error);
    }
  };

  // Handle card selection
  const handleCardClick = (buttonName) => {
    setSelectedButton(buttonName === selectedButton ? null : buttonName);
  };

  // Toggle day selection for recurring classes
  const toggleDay = (index) => {
    const newSelectedDays = [...selectedDays];
    newSelectedDays[index] = !newSelectedDays[index];
    setSelectedDays(newSelectedDays);
    // A day the class no longer runs on can't need two coaches
    if (!newSelectedDays[index] && twoCoachDays[index]) {
      const newTwoCoachDays = [...twoCoachDays];
      newTwoCoachDays[index] = false;
      setTwoCoachDays(newTwoCoachDays);
    }
  };

  const toggleTwoCoachDay = (index) => {
    const newTwoCoachDays = [...twoCoachDays];
    newTwoCoachDays[index] = !newTwoCoachDays[index];
    setTwoCoachDays(newTwoCoachDays);
  };

  // Only days the class runs on can be marked as two-coach days
  const renderTwoCoachButtons = () => {
    const days = ["S", "M", "T", "W", "T", "F", "S"];
    return days.map((day, index) =>
      selectedDays[index] ? (
        <button
          key={index}
          type="button"
          className={`day-button ${twoCoachDays[index] ? "selected" : ""}`}
          aria-label={`Two coaches on ${WEEKDAY_NAMES[index]}`}
          onClick={() => toggleTwoCoachDay(index)}
        >
          {day}
        </button>
      ) : null
    );
  };

  // Day of week buttons
  const renderDayButtons = () => {
    const days = ["S", "M", "T", "W", "T", "F", "S"];
    return days.map((day, index) => (
      <button
        key={index}
        className={`day-button ${selectedDays[index] ? "selected" : ""} ${
          selectedButton !== "reoccurring" ? "disabled" : ""
        }`}
        disabled={selectedButton !== "reoccurring"}
        onClick={() => toggleDay(index)}
      >
        {day}
      </button>
    ));
  };

  // Animation variants for framer-motion
  const cardVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4,
        ease: "easeOut",
      },
    },
    exit: {
      opacity: 0,
      y: -20,
      transition: {
        duration: 0.3,
        ease: "easeIn",
      },
    },
  };

  const cardHeaderVariants = {
    rest: { scale: 1 },
    hover: { scale: 1.02 },
  };

  return (
    <motion.div
      className="add-class-page"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      <div className="header-container">
        <h1 className="page-title">Class Manager</h1>
        <p className="subtitle">Create, schedule, and manage your classes</p>
      </div>

      <div className="card-container">
        {/* Single Class Card */}
        <motion.div
          className={`option-card ${
            selectedButton === "single" ? "selected" : ""
          }`}
          initial="hidden"
          animate="visible"
          variants={cardVariants}
          layout
        >
          <motion.div
            className="card-header"
            onClick={() => handleCardClick("single")}
            whileHover="hover"
            variants={cardHeaderVariants}
          >
            <div className="card-header-content">
              <div className="card-icon">
                <FaCalendarDay />
              </div>
              <h3 className="card-title">Single Class</h3>
            </div>
            <FiChevronDown className="toggle-icon" />
          </motion.div>

          <motion.div
            className="card-body"
            animate={{
              height: selectedButton === "single" ? "auto" : 0,
              opacity: selectedButton === "single" ? 1 : 0,
            }}
            transition={{ duration: 0.4, ease: [0.04, 0.62, 0.23, 0.98] }}
          >
            <div className="card-content" ref={inputContainerRef}>
              <div className="form-group">
                <label className="form-label">Class Type</label>
                <select
                  name="classType"
                  className="form-control"
                  onChange={handleSingleClassInputChange}
                  value={singleClassDetails.classType}
                >
                  <option value="" disabled>
                    Select Class Type
                  </option>
                  {Object.entries(classTypes).map(([key, { name }]) => (
                    <option key={key} value={key}>
                      {name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label className="form-label">Date</label>
                <input
                  type="date"
                  name="date"
                  className="form-control"
                  onChange={handleSingleClassInputChange}
                  value={singleClassDetails.date}
                />
              </div>

              <div className="form-group">
                <label className="form-label">Start Time</label>
                <input
                  type="time"
                  name="startTime"
                  className="form-control"
                  onChange={handleSingleClassInputChange}
                  value={singleClassDetails.startTime}
                />
              </div>

              <div className="form-group">
                <label className="form-label">Duration</label>
                <div className="duration-picker">
                  {DURATION_PRESETS.map((mins) => (
                    <motion.button
                      key={mins}
                      type="button"
                      className={`duration-button ${
                        !singleClassDetails.useCustomDuration &&
                        singleClassDetails.duration === mins
                          ? "selected"
                          : ""
                      }`}
                      onClick={() =>
                        setSingleClassDetails({
                          ...singleClassDetails,
                          duration: mins,
                          useCustomDuration: false,
                        })
                      }
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      {mins}
                    </motion.button>
                  ))}
                </div>
                <div className="custom-duration-row">
                  <label className="custom-duration-label">
                    <input
                      type="radio"
                      checked={singleClassDetails.useCustomDuration}
                      onChange={() =>
                        setSingleClassDetails({
                          ...singleClassDetails,
                          useCustomDuration: true,
                        })
                      }
                    />
                    <span>Custom:</span>
                  </label>
                  <input
                    type="number"
                    min="15"
                    max="240"
                    className="form-control custom-duration-input"
                    value={
                      singleClassDetails.useCustomDuration
                        ? singleClassDetails.duration
                        : ""
                    }
                    placeholder="min"
                    disabled={!singleClassDetails.useCustomDuration}
                    onChange={(e) =>
                      setSingleClassDetails({
                        ...singleClassDetails,
                        duration: parseInt(e.target.value) || DEFAULT_DURATION,
                        useCustomDuration: true,
                      })
                    }
                  />
                  <span className="duration-unit">min</span>
                </div>
              </div>

              {/* End Time Preview */}
              {singleClassDetails.startTime && (
                <motion.div
                  className="time-preview"
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <FaClock className="preview-icon" />
                  <span className="preview-text">
                    {formatTimePreview(singleClassDetails.startTime)} →{" "}
                    <strong>{formatTimePreview(singleEndTime)}</strong>
                  </span>
                </motion.div>
              )}

              {/* Overlap Warning */}
              <AnimatePresence>
                {singleClassOverlap.hasOverlap && (
                  <motion.div
                    className="overlap-warning"
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    <FaExclamationTriangle className="warning-icon" />
                    <div className="warning-content">
                      <span className="warning-title">Schedule Conflict</span>
                      <span className="warning-text">
                        {formatOverlapWarning(
                          singleClassOverlap.overlappingEvents
                        )}
                      </span>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              <motion.button
                className="submit-button"
                onClick={submitSingleClass}
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
              >
                <FaPlus />
                <span>Add Class</span>
              </motion.button>
            </div>
          </motion.div>
        </motion.div>

        {/* Recurring Class Card */}
        <motion.div
          className={`option-card ${
            selectedButton === "reoccurring" ? "selected" : ""
          }`}
          initial="hidden"
          animate="visible"
          variants={cardVariants}
          layout
          transition={{ delay: 0.1 }}
        >
          <motion.div
            className="card-header"
            onClick={() => handleCardClick("reoccurring")}
            whileHover="hover"
            variants={cardHeaderVariants}
          >
            <div className="card-header-content">
              <div className="card-icon">
                <FaCalendarWeek />
              </div>
              <h3 className="card-title">Recurring Class</h3>
            </div>
            <FiChevronDown className="toggle-icon" />
          </motion.div>

          <motion.div
            className="card-body"
            animate={{
              height: selectedButton === "reoccurring" ? "auto" : 0,
              opacity: selectedButton === "reoccurring" ? 1 : 0,
            }}
            transition={{ duration: 0.4, ease: [0.04, 0.62, 0.23, 0.98] }}
          >
            <div className="card-content" ref={reoccurringInputContainerRef}>
              <div className="form-group">
                <label className="form-label">Class Type</label>
                <select
                  name="classType"
                  className="form-control"
                  onChange={handleRecurringClassInputChange}
                  value={recurringClassDetails.classType}
                >
                  <option value="" disabled>
                    Select Class Type
                  </option>
                  {Object.entries(classTypes).map(([key, { name }]) => (
                    <option key={key} value={key}>
                      {name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label className="form-label">Start Date</label>
                <input
                  type="date"
                  name="startDate"
                  className="form-control"
                  onChange={handleRecurringClassInputChange}
                  value={recurringClassDetails.startDate}
                />
              </div>

              <div className="form-group">
                <label className="form-label">Start Time</label>
                <input
                  type="time"
                  name="startTime"
                  className="form-control"
                  onChange={handleRecurringClassInputChange}
                  value={recurringClassDetails.startTime}
                />
              </div>

              <div className="form-group">
                <label className="form-label">Duration</label>
                <div className="duration-picker">
                  {DURATION_PRESETS.map((mins) => (
                    <motion.button
                      key={mins}
                      type="button"
                      className={`duration-button ${
                        !recurringClassDetails.useCustomDuration &&
                        recurringClassDetails.duration === mins
                          ? "selected"
                          : ""
                      }`}
                      onClick={() =>
                        setRecurringClassDetails({
                          ...recurringClassDetails,
                          duration: mins,
                          useCustomDuration: false,
                        })
                      }
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      {mins}
                    </motion.button>
                  ))}
                </div>
                <div className="custom-duration-row">
                  <label className="custom-duration-label">
                    <input
                      type="radio"
                      checked={recurringClassDetails.useCustomDuration}
                      onChange={() =>
                        setRecurringClassDetails({
                          ...recurringClassDetails,
                          useCustomDuration: true,
                        })
                      }
                    />
                    <span>Custom:</span>
                  </label>
                  <input
                    type="number"
                    min="15"
                    max="240"
                    className="form-control custom-duration-input"
                    value={
                      recurringClassDetails.useCustomDuration
                        ? recurringClassDetails.duration
                        : ""
                    }
                    placeholder="min"
                    disabled={!recurringClassDetails.useCustomDuration}
                    onChange={(e) =>
                      setRecurringClassDetails({
                        ...recurringClassDetails,
                        duration: parseInt(e.target.value) || DEFAULT_DURATION,
                        useCustomDuration: true,
                      })
                    }
                  />
                  <span className="duration-unit">min</span>
                </div>
              </div>

              {/* End Time Preview */}
              {recurringClassDetails.startTime && (
                <motion.div
                  className="time-preview"
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <FaClock className="preview-icon" />
                  <span className="preview-text">
                    {formatTimePreview(recurringClassDetails.startTime)} →{" "}
                    <strong>{formatTimePreview(recurringEndTime)}</strong>
                  </span>
                </motion.div>
              )}

              {/* Overlap Warning */}
              <AnimatePresence>
                {recurringClassOverlap.hasOverlap && (
                  <motion.div
                    className="overlap-warning"
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    <FaExclamationTriangle className="warning-icon" />
                    <div className="warning-content">
                      <span className="warning-title">Schedule Conflict</span>
                      <span className="warning-text">
                        {formatRecurringOverlapWarning(
                          recurringClassOverlap.overlappingEvents
                        )}
                      </span>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              <div className="form-group">
                <label className="form-label">Select Days</label>
                <div className="weekday-picker">{renderDayButtons()}</div>
              </div>

              {selectedDays.some((d) => d) && (
                <div className="form-group">
                  <label className="form-label">Two Coaches On</label>
                  <div className="weekday-picker two-coach-picker">{renderTwoCoachButtons()}</div>
                  <small className="form-hint">
                    Leave a day off for one coach. You can change this later in
                    Coaches per Class.
                  </small>
                </div>
              )}

              <motion.button
                className="submit-button"
                onClick={submitRecurringClass}
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
              >
                <FaPlus />
                <span>Add Recurring Class</span>
              </motion.button>
            </div>
          </motion.div>
        </motion.div>

        {/* Delete Class Card */}
        <motion.div
          className={`option-card delete-card ${
            selectedButton === "delete" ? "selected" : ""
          }`}
          initial="hidden"
          animate="visible"
          variants={cardVariants}
          layout
          transition={{ delay: 0.2 }}
        >
          <motion.div
            className="card-header"
            onClick={() => handleCardClick("delete")}
            whileHover="hover"
            variants={cardHeaderVariants}
          >
            <div className="card-header-content">
              <div className="card-icon">
                <FaTrash />
              </div>
              <h3 className="card-title">Delete Class</h3>
            </div>
            <FiChevronDown className="toggle-icon" />
          </motion.div>

          <motion.div
            className="card-body"
            animate={{
              height: selectedButton === "delete" ? "auto" : 0,
              opacity: selectedButton === "delete" ? 1 : 0,
            }}
            transition={{ duration: 0.4, ease: [0.04, 0.62, 0.23, 0.98] }}
          >
            <div className="card-content">
              <div className="form-group">
                <label className="form-label">Select Date</label>
                <div className="date-selector">
                  <FaCalendarAlt className="date-icon" />
                  <input
                    type="date"
                    value={selectedDate}
                    onChange={handleDateChange}
                    className="form-control"
                  />
                </div>
              </div>

              <AnimatePresence>
                {isLoading ? (
                  <div className="loading-container">
                    <p>Loading events...</p>
                  </div>
                ) : (
                  <motion.div
                    className="events-container"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{ duration: 0.3 }}
                  >
                    <EventList
                      events={combinedEvents}
                      view="Delete"
                      dropClaimedEvent={deleteEvent}
                    />
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </motion.div>
        </motion.div>

        {/* Coaches per Class Card */}
        <motion.div
          className={`option-card ${
            selectedButton === "coaches" ? "selected" : ""
          }`}
          initial="hidden"
          animate="visible"
          variants={cardVariants}
          layout
          transition={{ delay: 0.3 }}
        >
          <motion.div
            className="card-header"
            onClick={() => handleCardClick("coaches")}
            whileHover="hover"
            variants={cardHeaderVariants}
          >
            <div className="card-header-content">
              <div className="card-icon">
                <FaUserFriends />
              </div>
              <h3 className="card-title">Coaches per Class</h3>
            </div>
            <FiChevronDown className="toggle-icon" />
          </motion.div>

          <motion.div
            className="card-body"
            animate={{
              height: selectedButton === "coaches" ? "auto" : 0,
              opacity: selectedButton === "coaches" ? 1 : 0,
            }}
            transition={{ duration: 0.4, ease: [0.04, 0.62, 0.23, 0.98] }}
          >
            <div className="card-content">
              <div className="form-group">
                <label className="form-label">Select Date</label>
                <div className="date-selector">
                  <FaCalendarAlt className="date-icon" />
                  <input
                    type="date"
                    value={coachCountDate}
                    onChange={(e) => setCoachCountDate(e.target.value)}
                    className="form-control"
                  />
                </div>
              </div>
              {selectedButton === "coaches" && coachCountDate && (
                <CoachCountList date={coachCountDate} />
              )}
            </div>
          </motion.div>
        </motion.div>
      </div>

      {/* Overlap Confirmation Modal */}
      <OverlapConfirmationModal
        show={showOverlapModal}
        onHide={() => {
          setShowOverlapModal(false);
          setPendingSubmission(null);
        }}
        onConfirm={handleConfirmOverlap}
        overlappingEvents={
          pendingSubmission?.type === "single"
            ? singleClassOverlap.overlappingEvents
            : recurringClassOverlap.overlappingEvents
        }
      />
    </motion.div>
  );
};

export default AddClassPage;
